'use client'

import { useEffect, useState } from 'react'

const parseValue = (value: unknown) => {
  if (typeof value !== 'string') return value

  try {
    return JSON.parse(value)
  } catch {
    return value
  }
}

const stringifyValue = (value: unknown) => {
  if (typeof value === 'string') return value

  try {
    return JSON.stringify(value)
  } catch {
    return ''
  }
}

export function useLocalStorage<D = null>(
  key: string,
  defaultValue: D
): [D, (newValue: D) => void] {
  const [value, setValue] = useState<D>(defaultValue)

  useEffect(() => {
    const savedValue = localStorage.getItem(key)
    if (savedValue !== null) {
      setValue(parseValue(savedValue) ?? defaultValue)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  const updateValue = (newValue: D) => {
    setValue((prevValue) => {
      let evaluatedValue = newValue
      if (typeof newValue === 'function') {
        evaluatedValue = newValue(prevValue)
      }

      const parsedValue = parseValue(evaluatedValue)

      localStorage.setItem(key, stringifyValue(parsedValue))
      return parsedValue
    })
  }

  return [value, updateValue]
}
